// =============================================================================
// MeuDriver — Hook de validação de CPF
// =============================================================================
// Responsabilidades:
//  1. Validação algorítmica dos dígitos verificadores (sem chamada de rede)
//  2. Verificação de unicidade via RPC validate_cpf_unique (com debounce)
//
// Uso:
//   const { valid, message, checking } = useCpfValidation(cpfValue)
//
// O campo cpfValue pode ser formatado (123.456.789-09) ou só dígitos.
// =============================================================================

import { useState, useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase'

// ---------------------------------------------------------------------------
// Funções puras de validação
// ---------------------------------------------------------------------------

/** Remove tudo que não é dígito. */
function onlyDigits(value: string): string {
  return value.replace(/[^0-9]/g, '')
}

/**
 * Aplica máscara CPF: 999.999.999-99.
 * Retorna o valor com máscara (útil para exibição).
 */
export function maskCpf(value: string): string {
  const digits = onlyDigits(value).slice(0, 11)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`
}

/**
 * Valida o algoritmo de dígitos verificadores do CPF brasileiro.
 * Retorna true somente se o CPF for algoritmicamente válido.
 */
export function isCpfAlgorithmValid(cpf: string): boolean {
  const digits = onlyDigits(cpf)

  if (digits.length !== 11) return false

  // CPFs com todos os dígitos iguais são inválidos (ex: 111.111.111-11)
  if (/^(\d)\1{10}$/.test(digits)) return false

  const calcDigit = (slice: string, factor: number): number => {
    let sum = 0
    for (const ch of slice) {
      sum += parseInt(ch, 10) * factor
      factor--
    }
    const remainder = (sum * 10) % 11
    return remainder === 10 || remainder === 11 ? 0 : remainder
  }

  const d1 = calcDigit(digits.slice(0, 9), 10)
  if (d1 !== parseInt(digits[9], 10)) return false

  const d2 = calcDigit(digits.slice(0, 10), 11)
  return d2 === parseInt(digits[10], 10)
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface CpfValidationState {
  /** CPF é algoritmicamente válido E (se já verificado) está disponível. */
  valid: boolean
  /** Mensagem de erro ou vazia quando válido. */
  message: string
  /** true enquanto a RPC de unicidade está em andamento. */
  checking: boolean
}

const DEBOUNCE_MS = 600

/**
 * Hook que valida um CPF em tempo real.
 *
 * @param cpf   Valor do campo (com ou sem formatação).
 * @param skip  Quando true, pula a verificação de unicidade (ex: campo opcional
 *              cujo valor ainda não foi preenchido).
 */
export function useCpfValidation(cpf: string, skip = false): CpfValidationState {
  const [checking, setChecking] = useState(false)
  const [uniqueOk, setUniqueOk] = useState<boolean | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastCheckedRef = useRef<string>('')

  const digits = onlyDigits(cpf)
  const algorithmOk = digits.length === 11 && isCpfAlgorithmValid(digits)

  useEffect(() => {
    // Limpa timer anterior
    if (timerRef.current) clearTimeout(timerRef.current)

    // Reseta se campo vazio ou inválido algoritmicamente ou skip=true
    if (skip || !algorithmOk) {
      setUniqueOk(null)
      setChecking(false)
      return
    }

    // Evita checar o mesmo CPF duas vezes seguidas
    if (digits === lastCheckedRef.current) return

    setChecking(true)
    timerRef.current = setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc('validate_cpf_unique', {
          p_cpf: digits,
        })
        if (error) {
          // Em caso de erro de rede/RPC, não bloqueia o usuário
          setUniqueOk(true)
        } else {
          setUniqueOk(data as boolean)
        }
        lastCheckedRef.current = digits
      } finally {
        setChecking(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [digits, algorithmOk, skip])

  // Calcula resultado final
  if (skip || digits.length === 0) {
    return { valid: true, message: '', checking: false }
  }

  if (digits.length < 11) {
    return { valid: false, message: 'CPF incompleto.', checking: false }
  }

  if (!algorithmOk) {
    return { valid: false, message: 'CPF inválido.', checking: false }
  }

  if (checking) {
    return { valid: false, message: '', checking: true }
  }

  if (uniqueOk === false) {
    return { valid: false, message: 'Este CPF já está cadastrado.', checking: false }
  }

  return { valid: uniqueOk === true, message: '', checking: false }
}
